import { Megaphone, Sparkles, AlertTriangle, CheckCircle2, ExternalLink, X } from 'lucide-react';
import { cn } from 'utils/cn';
import type { Announcement, AnnouncementVariant } from 'shared/data/repositories/announcementRepository';
import {
  useDismissAnnouncement,
  useVisibleAnnouncements,
} from 'features/announcements/hooks/useAnnouncements';

const VARIANT_META: Record<
  AnnouncementVariant,
  { Icon: typeof Megaphone; label: string }
> = {
  info: { Icon: Megaphone, label: 'Announcement' },
  promo: { Icon: Sparkles, label: 'Promotion' },
  urgent: { Icon: AlertTriangle, label: 'Important' },
  success: { Icon: CheckCircle2, label: 'Update' },
};

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function scopeSuffix(scope: Announcement['scope']): string {
  if (scope === 'global') return ' · Platform';
  if (scope === 'batch') return ' · Batch';
  return '';
}

function AnnouncementRow({
  item,
  onDismiss,
  dismissing,
}: {
  item: Announcement;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  const variant = item.variant in VARIANT_META ? item.variant : 'info';
  const { Icon, label } = VARIANT_META[variant];
  const hasCta = Boolean(item.ctaLabel && item.ctaUrl);

  return (
    <div className="announcement-banner">
      <div
        className={cn(
          'announcement-strip motion-safe:animate-[fadeIn_0.3s_ease-out]',
          `announcement-strip--${variant}`
        )}
        role="status"
        aria-live="polite"
      >
        <div className="announcement-strip__inner">
          <div className="announcement-strip__icon" aria-hidden>
            <Icon className="h-4 w-4" strokeWidth={2} />
          </div>

          <span className="announcement-strip__meta">
            {label}
            {scopeSuffix(item.scope)}
          </span>

          <span className="announcement-strip__title">{item.title}</span>

          <span className="announcement-strip__body">{item.body}</span>

          <div className="announcement-strip__actions">
            {hasCta ? (
              <a
                href={item.ctaUrl!}
                target={isExternalUrl(item.ctaUrl!) ? '_blank' : undefined}
                rel={isExternalUrl(item.ctaUrl!) ? 'noopener noreferrer' : undefined}
                className="announcement-strip__cta"
              >
                {item.ctaLabel}
                {isExternalUrl(item.ctaUrl!) ? (
                  <ExternalLink className="h-3 w-3" aria-hidden />
                ) : null}
              </a>
            ) : null}

            {item.dismissible ? (
              <button
                type="button"
                onClick={onDismiss}
                disabled={dismissing}
                className="announcement-strip__dismiss disabled:opacity-50"
                aria-label="Dismiss announcement"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Full-width strip below TopNav. Pushes content down (does not overlay). */
export function AnnouncementBanner() {
  const { data } = useVisibleAnnouncements();
  const items: Announcement[] = data ?? [];
  const dismissMutation = useDismissAnnouncement();

  if (items.length === 0) return null;

  return (
    <div className="relative z-[calc(var(--z-header)-1)] shrink-0">
      {items.map((item) => (
        <AnnouncementRow
          key={item.id}
          item={item}
          dismissing={dismissMutation.isPending && dismissMutation.variables === item.id}
          onDismiss={() => dismissMutation.mutate(item.id)}
        />
      ))}
    </div>
  );
}
